import { useState, useRef, useEffect, useCallback } from "react";
import "@/App.css";
import { useTheme } from "@/hooks/useTheme";
import { useAppData } from "@/hooks/useAppData";
import { useAuth } from "@/hooks/useAuth";
import { usePdfExport } from "@/hooks/usePdfExport";
import Connect from "@/components/Connect";
import Landing from "@/components/Landing";
import AnalystReport from "@/components/AnalystReport";
import Chat from "@/components/Chat";
import Connections from "@/components/Connections";
import AnalyticsSection from "@/components/AnalyticsSection";
import AdminPortal from "@/components/AdminPortal";
import PrintReport from "@/components/PrintReport";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import SupportDialog from "@/components/SupportDialog";
import { RANGE_OPTIONS } from "@/lib/dateRanges";
import { beaconCleanup } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

function App() {
  const { theme, toggle } = useTheme();
  const { user, ready: authReady, login, register, logout, isAdmin } = useAuth();
  const isAuthenticated = !!user;
  const {
    loading,
    status,
    analytics,
    report,
    syncing,
    range,
    rangeLoading,
    setRange,
    loadAll,
    sync,
    disconnect,
    setReport,
  } = useAppData();
  const [tab, setTab] = useState("connect");
  const [supportOpen, setSupportOpen] = useState(false);
  const printRef = useRef(null);
  const { exportPdf, printReport } = usePdfExport(printRef, analytics);

  // Context-aware export registry: each tab registers its {exportPdf, printFn}
  // so the single header PDF/Print pair acts on whatever tab is active.
  const exportersRef = useRef({});
  const [headerBusy, setHeaderBusy] = useState({ pdf: false, print: false });
  const registerExporter = useCallback((key, handlers) => {
    exportersRef.current[key] = handlers;
  }, []);

  // AI Analyst exports the sales report layout. (The Analytics Overview
  // dashboard export is wired via AnalyticsSection's overviewExport prop.)
  useEffect(() => {
    registerExporter("analyst", { exportPdf, printFn: printReport });
  }, [registerExporter, exportPdf, printReport]);

  const runHeaderExport = useCallback(
    async (kind) => {
      const ex = exportersRef.current[tab];
      const fn = ex && (kind === "pdf" ? ex.exportPdf : ex.printFn);
      if (!fn) {
        toast.error("Nothing to export on this tab.");
        return;
      }
      setHeaderBusy((b) => ({ ...b, [kind]: true }));
      try {
        await fn();
      } finally {
        setHeaderBusy((b) => ({ ...b, [kind]: false }));
      }
    },
    [tab]
  );

  // Best-effort: purge this session's data on the server when the tab is closed
  // or hidden (navigated away), so nothing is retained after the user exits.
  useEffect(() => {
    const onExit = () => beaconCleanup();
    window.addEventListener("pagehide", onExit);
    window.addEventListener("beforeunload", onExit);
    return () => {
      window.removeEventListener("pagehide", onExit);
      window.removeEventListener("beforeunload", onExit);
    };
  }, []);

  // After a successful connect/sync, load data but STAY on the Connect tab.
  // The full tab bar is revealed once connected; the user navigates to
  // Analytics (and other tabs) themselves.
  const handleConnected = useCallback(async () => {
    await loadAll();
  }, [loadAll]);

  // When connected, work in the app. When the connection drops, return to Connect tab.
  useEffect(() => {
    if (!status?.connected) setTab("connect");
  }, [status?.connected]);

  // If a suspended/expired token logs the user out while on the admin tab, or a
  // non-admin somehow lands there, bounce to Connect.
  useEffect(() => {
    if (tab === "admin" && !isAdmin) setTab("connect");
  }, [tab, isAdmin]);

  // Header logo "home": go to the Connect tab (or, if not connected, it's the
  // landing content on the Connect tab).
  const onLogoHome = useCallback(() => {
    setTab("connect");
  }, []);

  const handleLogout = useCallback(() => {
    disconnect?.();       // clear any ephemeral CRM session server-side
    logout();             // clear the JWT -> returns to Landing
    setTab("connect");
  }, [disconnect, logout]);

  // Wait for BOTH the auth check and initial app-data load before rendering.
  if (!authReady || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not logged in -> marketing Landing with Log in / Sign up (registration is
  // required before connecting HubSpot). Auth does not change HubSpot's
  // read-only nature or persist any CRM data.
  if (!isAuthenticated) {
    return (
      <>
        <Landing
          theme={theme}
          onToggleTheme={toggle}
          onLogin={login}
          onRegister={register}
        />
        <Toaster richColors position="top-right" theme={theme} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader
        status={status}
        theme={theme}
        onToggleTheme={toggle}
        onSync={sync}
        syncing={syncing}
        onDisconnect={disconnect}
        onExportPdf={() => runHeaderExport("pdf")}
        exporting={headerBusy.pdf}
        onPrint={() => runHeaderExport("print")}
        printing={headerBusy.print}
        tab={tab}
        setTab={setTab}
        onLogoHome={onLogoHome}
        user={user}
        isAdmin={isAdmin}
        onLogout={handleLogout}
        onContactSupport={() => setSupportOpen(true)}
      />

      <main className="max-w-7xl mx-auto w-full px-6 py-6 flex-1">
        {status.warnings?.length > 0 && tab === "connect" && status.connected && (
          <div className="mb-4 rounded-lg bg-primary/5 border border-primary/20 px-4 py-2.5 text-sm text-foreground/80">
            {status.warnings.map((w) => (
              <p key={w}>• {w}</p>
            ))}
          </div>
        )}
        {tab === "connect" && <Connect onConnected={handleConnected} status={status} onGoToAnalytics={() => setTab("analytics")} />}
        {tab === "connections" && <Connections source={status.source} onRegisterExport={registerExporter} />}
        {tab === "analytics" && (
          <AnalyticsSection
            range={range}
            onRangeChange={setRange}
            rangeLoading={rangeLoading}
            onRegisterExport={registerExporter}
            overviewExport={{ exportPdf, printFn: printReport }}
            theme={theme}
            analytics={analytics}
          />
        )}
        {tab === "chat" && <Chat onIncludedChange={() => {}} onRegisterExport={registerExporter} />}
        {tab === "analyst" && (
          <AnalystReport
            initialReport={report?.report_md}
            initialGeneratedAt={report?.generated_at}
            onReportGenerated={(md, at) => setReport({ report_md: md, generated_at: at })}
            onReportCleared={() => setReport({ report_md: null })}
          />
        )}
        {tab === "admin" && isAdmin && <AdminPortal currentUser={user} />}
      </main>

      <Footer />

      {/* Off-screen print layout for PDF export */}
      <PrintReport
        ref={printRef}
        analytics={analytics}
        reportMd={report?.report_md}
        generatedAt={report?.generated_at}
        rangeLabel={RANGE_OPTIONS.find((o) => o.value === range)?.label || "All Time"}
      />

      <Toaster richColors position="top-right" theme={theme} />

      <SupportDialog
        open={supportOpen}
        onOpenChange={setSupportOpen}
        mode="authenticated"
        user={user}
      />
    </div>
  );
}

export default App;
